import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { Formik } from "formik";
import { AppDispatch, AppState } from "../../../redux/store";
import PermissionGate from "../../../core/layouts/permission-gate";
import ToastComponent from "../../../components/toast";
import Input from "../../../components/input";
import InputFormat from "../../../components/input-format";
import DatePicker from "../../../components/date-picker";
import Button from "../../../components/button";
import StyledSelect from "../../../components/styled-select";
import { OptionValue } from "../../../types/component";
import { isSelectSingleValue } from "../../../core/utils";
import {
  createJDStateAction,
  clearCreateJDState,
  JDCreateRequest,
  JDStatus,
  JobType,
} from "../../../redux/features/jd-slice";
import { UserRole } from "../../../redux/features/user-slice";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../redux/features/common-slice";
import paths from "../../../navigation/paths";
import { addWeeks, format, parse } from "date-fns";
import { MultiValue, SingleValue } from "react-select";
import JobPreviewModal from "../job-preview-modal";

export default function AddJDPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const createJDState = useSelector(
    (state: AppState) => state.jds.createJDState
  );
  const errorState = useSelector(getErrorState);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);

  const handleSuccessToast = useCallback(
    (message: string, clearAction: any) => {
      toast(message, { type: "success" });
      setTimeout(() => {
        dispatch(clearAction());
      }, 2000);
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(
      setBreadcrumbState([
        { id: paths.JDS, path: paths.JDS, title: "Job Descriptions" },
        { id: "addJD", path: null, title: "Add Job Description" },
      ])
    );
  }, [dispatch]);

  useEffect(() => {
    if (createJDState.data && createJDState.data.success) {
      handleSuccessToast(createJDState.data.message, clearCreateJDState);
      setTimeout(() => {
        navigate(paths.JDS, { replace: true });
      }, 2000);
    }
  }, [createJDState, handleSuccessToast, navigate]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState, navigate]);

  const handleOpenPreview = () => {
    setOpenPreviewDialog(true);
  };

  const handleClosePreview = () => {
    setOpenPreviewDialog(false);
  };

  return (
    <PermissionGate
      categories={[]}
      roles={[UserRole.Admin, UserRole.Contributor]}
    >
      <Helmet title="Add Job Description" />
      <Formik
        initialValues={
          {
            title: "",
            description: "",
            requirements: "",
            deadline: format(new Date(), "yyyy-MM-dd"),
            status: JDStatus.Draft,
            jobType: JobType.FullTime,
            location: "",
          } as JDCreateRequest
        }
        validationSchema={Yup.object().shape({
          title: Yup.string()
            .required("Title is required")
            .max(100, "Title must be at most 100 characters"),
          description: Yup.string().max(5000, "Description is too long"),
          requirements: Yup.string().max(5000, "Requirements are too long"),
          status: Yup.string()
            .required("Status is required")
            .oneOf(Object.values(JDStatus)),
          jobType: Yup.string()
            .required("Job Type is required")
            .oneOf(Object.values(JobType)),
          location: Yup.string().max(
            100,
            "Location must be at most 100 characters"
          ),
        })}
        onSubmit={async (values, { setSubmitting }) => {
          await dispatch(createJDStateAction(values));
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          handleChange,
          handleSubmit,
          isSubmitting,
          setValues,
          dirty,
        }) => {
          const deadlineDate = values.deadline
            ? parse(values.deadline, "yyyy-MM-dd", new Date())
            : new Date();

          return (
            <>
              <div className="h-full overflow-y-auto">
                <form
                  noValidate
                  autoComplete="off"
                  onSubmit={handleSubmit}
                  className="h-full"
                >
                  <div
                    className="h-full grid grid-cols-1 sm:grid-cols-2 gap-8 overflow-y-scroll"
                    style={{
                      height: "calc(100% - 56px)",
                    }}
                  >
                    <div className="space-y-4">
                      <Input
                        label="Title *"
                        type="text"
                        id="title"
                        value={values.title}
                        onChange={handleChange}
                        errorText={errors.title}
                      />
                      <InputFormat
                        label="Description"
                        id="description-editor"
                        height="200px"
                        value={values.description}
                        onChange={(value) =>
                          setValues({ ...values, description: value })
                        }
                        errorText={errors.description}
                      />
                      <InputFormat
                        label="Requirements"
                        id="requirements-editor"
                        height="200px"
                        value={values.requirements}
                        onChange={(value) =>
                          setValues({ ...values, requirements: value })
                        }
                        errorText={errors.requirements}
                      />
                      <div className="space-y-1">
                        <label className="block text-xs text-text-title">
                          Deadline
                        </label>
                        <DatePicker
                          defaultValue={deadlineDate}
                          readOnly={false}
                          onChanged={(date) =>
                            setValues({
                              ...values,
                              deadline: date ? format(date, "yyyy-MM-dd") : "",
                            })
                          }
                        />
                      </div>
                      <div className="flex flex-col">
                        <label className="text-sm text-text-title">
                          Status *
                        </label>
                        <StyledSelect
                          options={Object.values(JDStatus).map((status) => ({
                            value: status,
                            label:
                              status.charAt(0).toUpperCase() + status.slice(1),
                          }))}
                          isClearable={false}
                          menuPosition="fixed"
                          value={{
                            value: values.status,
                            label:
                              values.status.charAt(0).toUpperCase() +
                              values.status.slice(1),
                          }}
                          onChange={(
                            value:
                              | SingleValue<OptionValue>
                              | MultiValue<OptionValue>
                          ) => {
                            if (value && isSelectSingleValue(value)) {
                              setValues({
                                ...values,
                                status: value.value as JDStatus,
                              });
                            }
                          }}
                        />
                      </div>
                      <div className="flex flex-col">
                        <label className="text-sm text-text-title">
                          Job Type *
                        </label>
                        <StyledSelect
                          options={Object.values(JobType).map((jobType) => ({
                            value: jobType,
                            label: jobType
                              .split("_")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() + word.slice(1)
                              )
                              .join(" "),
                          }))}
                          isClearable={false}
                          menuPosition="fixed"
                          value={{
                            value: values.jobType,
                            label: values.jobType
                              .split("_")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() + word.slice(1)
                              )
                              .join(" "),
                          }}
                          onChange={(
                            value:
                              | SingleValue<OptionValue>
                              | MultiValue<OptionValue>
                          ) => {
                            if (value && isSelectSingleValue(value)) {
                              setValues({
                                ...values,
                                jobType: value.value as JobType,
                              });
                            }
                          }}
                        />
                      </div>
                      <Input
                        label="Location"
                        type="text"
                        id="location"
                        value={values.location}
                        onChange={handleChange}
                        errorText={errors.location}
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 pt-4">
                    <Button
                      label="Submit"
                      type="submit"
                      loading={createJDState.loading}
                      disabled={!dirty || isSubmitting || createJDState.loading}
                    />
                    <Button
                      label="Preview"
                      type="button"
                      onClick={handleOpenPreview}
                      disabled={!dirty}
                    />
                  </div>
                </form>
              </div>
              <JobPreviewModal
                open={openPreviewDialog}
                onClose={handleClosePreview}
                job={values}
              />
              <ToastComponent id="addJD" />
            </>
          );
        }}
      </Formik>
    </PermissionGate>
  );
}